import React, { useState, useEffect, useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { apiString as API_URL } from '../../utils/constants';

export interface AiPromptSettingsDto {
    ChatAiSystemPrompt: string;
    MemberSummarySystemPrompt: string;
    TeamSummarySystemPrompt: string;
}

interface AiPromptsPanelProps {
    setToast: (toast: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
}

const AiPromptsPanel: React.FC<AiPromptsPanelProps> = ({ setToast }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);

    // Current edited state
    const [chatPrompt, setChatPrompt] = useState<string>('');
    const [memberSummaryPrompt, setMemberSummaryPrompt] = useState<string>('');
    const [teamSummaryPrompt, setTeamSummaryPrompt] = useState<string>('');

    // Saved state in DB
    const [savedPrompts, setSavedPrompts] = useState<AiPromptSettingsDto>({
        ChatAiSystemPrompt: '',
        MemberSummarySystemPrompt: '',
        TeamSummarySystemPrompt: '',
    });

    // Default baseline prompts from server
    const [defaultPrompts, setDefaultPrompts] = useState<AiPromptSettingsDto>({
        ChatAiSystemPrompt: '',
        MemberSummarySystemPrompt: '',
        TeamSummarySystemPrompt: '',
    });

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [savingSpecific, setSavingSpecific] = useState<'chat' | 'member' | 'team' | null>(null);

    // Confirmation modal for reset to default
    const [resetModalOpen, setResetModalOpen] = useState<boolean>(false);
    const [resetTarget, setResetTarget] = useState<'chat' | 'member' | 'team' | 'all'>('chat');

    const fetchPromptsData = async () => {
        try {
            setLoading(true);
            const [settingRes, defaultsRes] = await Promise.all([
                axios.get(`${API_URL}/setting/getsetting`),
                axios.get<AiPromptSettingsDto>(`${API_URL}/setting/defaultaiprompts`),
            ]);

            const currentChat = settingRes.data?.ChatAiSystemPrompt || defaultsRes.data?.ChatAiSystemPrompt || '';
            const currentMember = settingRes.data?.MemberSummarySystemPrompt || defaultsRes.data?.MemberSummarySystemPrompt || '';
            const currentTeam = settingRes.data?.TeamSummarySystemPrompt || defaultsRes.data?.TeamSummarySystemPrompt || '';

            setChatPrompt(currentChat);
            setMemberSummaryPrompt(currentMember);
            setTeamSummaryPrompt(currentTeam);

            setSavedPrompts({
                ChatAiSystemPrompt: currentChat,
                MemberSummarySystemPrompt: currentMember,
                TeamSummarySystemPrompt: currentTeam,
            });

            if (defaultsRes.data) {
                setDefaultPrompts(defaultsRes.data);
            }
        } catch (err: any) {
            console.error('Failed to fetch AI system prompts:', err);
            setToast({
                open: true,
                message: 'Failed to load AI system prompts.',
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPromptsData();
    }, []);

    const isChatDirty = useMemo(() => chatPrompt.trim() !== (savedPrompts.ChatAiSystemPrompt || '').trim(), [chatPrompt, savedPrompts]);
    const isMemberDirty = useMemo(() => memberSummaryPrompt.trim() !== (savedPrompts.MemberSummarySystemPrompt || '').trim(), [memberSummaryPrompt, savedPrompts]);
    const isTeamDirty = useMemo(() => teamSummaryPrompt.trim() !== (savedPrompts.TeamSummarySystemPrompt || '').trim(), [teamSummaryPrompt, savedPrompts]);
    const hasAnyChanges = isChatDirty || isMemberDirty || isTeamDirty;

    const isChatDefault = useMemo(() => chatPrompt.trim() === (defaultPrompts.ChatAiSystemPrompt || '').trim(), [chatPrompt, defaultPrompts]);
    const isMemberDefault = useMemo(() => memberSummaryPrompt.trim() === (defaultPrompts.MemberSummarySystemPrompt || '').trim(), [memberSummaryPrompt, defaultPrompts]);
    const isTeamDefault = useMemo(() => teamSummaryPrompt.trim() === (defaultPrompts.TeamSummarySystemPrompt || '').trim(), [teamSummaryPrompt, defaultPrompts]);

    const handleSaveAll = async () => {
        try {
            setSaving(true);
            const payload: AiPromptSettingsDto = {
                ChatAiSystemPrompt: chatPrompt,
                MemberSummarySystemPrompt: memberSummaryPrompt,
                TeamSummarySystemPrompt: teamSummaryPrompt,
            };

            await axios.put(`${API_URL}/setting/updateaiprompts`, payload);

            setSavedPrompts(payload);
            setToast({
                open: true,
                message: 'All AI System Prompts successfully saved.',
                severity: 'success',
            });
        } catch (err: any) {
            console.error('Failed to save AI prompts:', err);
            setToast({
                open: true,
                message: err.response?.data || 'Failed to save AI System Prompts.',
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveSpecific = async (type: 'chat' | 'member' | 'team') => {
        try {
            setSavingSpecific(type);
            const payload: AiPromptSettingsDto = {
                ChatAiSystemPrompt: type === 'chat' ? chatPrompt : savedPrompts.ChatAiSystemPrompt,
                MemberSummarySystemPrompt: type === 'member' ? memberSummaryPrompt : savedPrompts.MemberSummarySystemPrompt,
                TeamSummarySystemPrompt: type === 'team' ? teamSummaryPrompt : savedPrompts.TeamSummarySystemPrompt,
            };

            await axios.put(`${API_URL}/setting/updateaiprompts`, payload);

            setSavedPrompts(payload);
            const label = type === 'chat' ? 'Chart Chat AI' : type === 'member' ? 'Member Summary AI' : 'Team Summary AI';
            setToast({
                open: true,
                message: `${label} prompt successfully saved.`,
                severity: 'success',
            });
        } catch (err: any) {
            console.error('Failed to save AI prompt:', err);
            setToast({
                open: true,
                message: err.response?.data || 'Failed to save AI System Prompt.',
                severity: 'error',
            });
        } finally {
            setSavingSpecific(null);
        }
    };

    const handleConfirmReset = () => {
        if (resetTarget === 'chat') {
            setChatPrompt(defaultPrompts.ChatAiSystemPrompt);
        } else if (resetTarget === 'member') {
            setMemberSummaryPrompt(defaultPrompts.MemberSummarySystemPrompt);
        } else if (resetTarget === 'team') {
            setTeamSummaryPrompt(defaultPrompts.TeamSummarySystemPrompt);
        } else if (resetTarget === 'all') {
            setChatPrompt(defaultPrompts.ChatAiSystemPrompt);
            setMemberSummaryPrompt(defaultPrompts.MemberSummarySystemPrompt);
            setTeamSummaryPrompt(defaultPrompts.TeamSummarySystemPrompt);
        }
        setResetModalOpen(false);
        setToast({
            open: true,
            message: 'Prompt restored to baseline default template. Click Save to persist.',
            severity: 'success',
        });
    };

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        setToast({
            open: true,
            message: `${label} copied to clipboard.`,
            severity: 'success',
        });
    };

    const renderWordAndCharCount = (text: string) => {
        const words = text.trim() ? text.trim().split(/\s+/).length : 0;
        const chars = text.length;
        return `${words.toLocaleString()} words · ${chars.toLocaleString()} characters`;
    };

    if (loading) {
        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                <CircularProgress size={36} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Loading AI System Prompts...
                </Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={3}>
            {/* Header Card */}
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 247, 250, 0.9))',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <EditNoteIcon color="primary" sx={{ fontSize: 28 }} />
                            <Typography variant="h6" fontWeight="700">
                                AI System Prompts
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            Customize system instructions for Chart Chat, Member Summaries, and Team Summaries. Prompts dynamically take effect immediately upon saving.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchPromptsData}
                            disabled={loading || saving}
                            size="small"
                        >
                            Reload
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveAll}
                            disabled={saving || !hasAnyChanges}
                            size="small"
                        >
                            Save All Changes
                        </Button>
                    </Stack>
                </Stack>

                {hasAnyChanges && (
                    <Alert severity="warning" sx={{ mt: 2, py: 0.5, borderRadius: 2 }}>
                        You have unsaved prompt modifications. Be sure to click <strong>Save</strong> to apply your changes.
                    </Alert>
                )}

                {/* Navigation Tabs for Prompts */}
                <Box sx={{ mt: 3, borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={selectedTab}
                        onChange={(_, val) => setSelectedTab(val)}
                        textColor="primary"
                        indicatorColor="primary"
                    >
                        <Tab
                            icon={<ChatBubbleOutlineIcon fontSize="small" />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <span>Chart Chat AI</span>
                                    {isChatDirty && <Chip label="Modified" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                                </Stack>
                            }
                        />
                        <Tab
                            icon={<PersonOutlineIcon fontSize="small" />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <span>Member Summary AI</span>
                                    {isMemberDirty && <Chip label="Modified" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                                </Stack>
                            }
                        />
                        <Tab
                            icon={<GroupsOutlinedIcon fontSize="small" />}
                            iconPosition="start"
                            label={
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <span>Team Summary AI</span>
                                    {isTeamDirty && <Chip label="Modified" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />}
                                </Stack>
                            }
                        />
                    </Tabs>
                </Box>
            </Paper>

            {/* Tab 0: Chart Chat AI Prompt */}
            {selectedTab === 0 && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <Stack spacing={2.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" fontWeight="700">
                                        Chart Assistant System Prompt
                                    </Typography>
                                    {isChatDefault ? (
                                        <Chip label="Baseline Default" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    ) : (
                                        <Chip label="Customized" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {isChatDirty && (
                                        <Chip label="Unsaved Changes" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                </Stack>
                                <Typography variant="caption" color="textSecondary">
                                    Instructs the conversational chart assistant on tone, storytelling structure, clinical grounding, and conversational rules during interactive chart Q&A.
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                                {renderWordAndCharCount(chatPrompt)}
                            </Typography>
                        </Stack>

                        <TextField
                            fullWidth
                            multiline
                            minRows={10}
                            maxRows={24}
                            value={chatPrompt}
                            onChange={(e) => setChatPrompt(e.target.value)}
                            variant="outlined"
                            placeholder="Enter Chart Chat AI system prompt..."
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: '"Fira Code", "Roboto Mono", Consolas, "Courier New", monospace',
                                    fontSize: '0.88rem',
                                    lineHeight: 1.6,
                                    backgroundColor: '#fafbfc',
                                },
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    startIcon={<RestartAltIcon />}
                                    onClick={() => {
                                        setResetTarget('chat');
                                        setResetModalOpen(true);
                                    }}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopy(chatPrompt, 'Chart Chat AI Prompt')}
                                >
                                    Copy
                                </Button>
                            </Stack>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={savingSpecific === 'chat' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                disabled={savingSpecific === 'chat' || !isChatDirty}
                                onClick={() => handleSaveSpecific('chat')}
                            >
                                Save Chart Prompt
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Tab 1: Member Summary AI Prompt */}
            {selectedTab === 1 && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <Stack spacing={2.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" fontWeight="700">
                                        Member Summary AI System Prompt
                                    </Typography>
                                    {isMemberDefault ? (
                                        <Chip label="Baseline Default" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    ) : (
                                        <Chip label="Customized" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {isMemberDirty && (
                                        <Chip label="Unsaved Changes" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                </Stack>
                                <Typography variant="caption" color="textSecondary">
                                    Defines the format for generating the single-paragraph snapshot of a member's accomplishments, 30-day successes, current goals, and barriers.
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                                {renderWordAndCharCount(memberSummaryPrompt)}
                            </Typography>
                        </Stack>

                        <TextField
                            fullWidth
                            multiline
                            minRows={12}
                            maxRows={24}
                            value={memberSummaryPrompt}
                            onChange={(e) => setMemberSummaryPrompt(e.target.value)}
                            variant="outlined"
                            placeholder="Enter Member Summary AI system prompt..."
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: '"Fira Code", "Roboto Mono", Consolas, "Courier New", monospace',
                                    fontSize: '0.88rem',
                                    lineHeight: 1.6,
                                    backgroundColor: '#fafbfc',
                                },
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    startIcon={<RestartAltIcon />}
                                    onClick={() => {
                                        setResetTarget('member');
                                        setResetModalOpen(true);
                                    }}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopy(memberSummaryPrompt, 'Member Summary Prompt')}
                                >
                                    Copy
                                </Button>
                            </Stack>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={savingSpecific === 'member' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                disabled={savingSpecific === 'member' || !isMemberDirty}
                                onClick={() => handleSaveSpecific('member')}
                            >
                                Save Member Summary Prompt
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Tab 2: Team Summary AI Prompt */}
            {selectedTab === 2 && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <Stack spacing={2.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="subtitle1" fontWeight="700">
                                        Team Summary AI System Prompt
                                    </Typography>
                                    {isTeamDefault ? (
                                        <Chip label="Baseline Default" size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    ) : (
                                        <Chip label="Customized" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {isTeamDirty && (
                                        <Chip label="Unsaved Changes" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                </Stack>
                                <Typography variant="caption" color="textSecondary">
                                    Controls care team summary generation with a brief team overview followed by dedicated conversational paragraphs for each top ranked active member, evaluating recent Progress Notes, Engagement Notes, Appointment Notes, Chart Warnings, and clinical gaps.
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                                {renderWordAndCharCount(teamSummaryPrompt)}
                            </Typography>
                        </Stack>

                        <TextField
                            fullWidth
                            multiline
                            minRows={12}
                            maxRows={24}
                            value={teamSummaryPrompt}
                            onChange={(e) => setTeamSummaryPrompt(e.target.value)}
                            variant="outlined"
                            placeholder="Enter Team Summary AI system prompt..."
                            sx={{
                                '& .MuiInputBase-root': {
                                    fontFamily: '"Fira Code", "Roboto Mono", Consolas, "Courier New", monospace',
                                    fontSize: '0.88rem',
                                    lineHeight: 1.6,
                                    backgroundColor: '#fafbfc',
                                },
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    startIcon={<RestartAltIcon />}
                                    onClick={() => {
                                        setResetTarget('team');
                                        setResetModalOpen(true);
                                    }}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopy(teamSummaryPrompt, 'Team Summary Prompt')}
                                >
                                    Copy
                                </Button>
                            </Stack>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={savingSpecific === 'team' ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                disabled={savingSpecific === 'team' || !isTeamDirty}
                                onClick={() => handleSaveSpecific('team')}
                            >
                                Save Team Summary Prompt
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {/* Reset to Default Confirmation Modal */}
            <Dialog open={resetModalOpen} onClose={() => setResetModalOpen(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Reset Prompt to Default Baseline?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to replace this prompt with the system baseline default template?
                        <br /><br />
                        Any custom prompt text you have typed will be overwritten in the editor. You can then review the default text and click <strong>Save</strong> to confirm.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResetModalOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmReset} color="warning" variant="contained">
                        Reset to Default
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default AiPromptsPanel;
